import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@acitra/i18n';
import { useAuthStore } from '../stores/auth-store';
import {
  listInvoices,
  getInvoiceUploadUrl,
  uploadInvoiceToS3,
  deleteInvoice,
  retryExtraction,
  retryMatching,
  type InvoiceListItem,
  type InvoiceStatus,
} from '../services/api';
import { useWebSocketSubscription } from '../hooks/useWebSocket';
import { DeleteInvoiceModal } from '../components/DeleteInvoiceModal';
import { ReprocessInvoiceModal } from '../components/ReprocessInvoiceModal';
import { RunMatchingModal } from '../components/RunMatchingModal';

type StatusFilter = 'all' | InvoiceStatus;

type SortColumn =
  | 'invoiceNumber'
  | 'vendor'
  | 'invoiceDate'
  | 'dueDate'
  | 'total'
  | 'currency'
  | 'status'
  | 'createdAt';
type SortDirection = 'asc' | 'desc';

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  pending_approval: 'bg-purple-100 text-purple-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
  extraction_failed: 'bg-red-100 text-red-800',
  DUPLICATE_FILE: 'bg-orange-100 text-orange-800',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  processing: 'Processing',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  rejected: 'Rejected',
  extraction_failed: 'Failed',
  DUPLICATE_FILE: 'Duplicate',
};

export function Invoices() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentTenantId } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState<SortColumn>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showUploadArea, setShowUploadArea] = useState(false);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<InvoiceListItem | null>(null);

  // Reprocess state
  const [reprocessTarget, setReprocessTarget] = useState<InvoiceListItem | null>(null);
  const [isReprocessing, setIsReprocessing] = useState(false);

  // Matching state
  const [matchingTarget, setMatchingTarget] = useState<InvoiceListItem | null>(null);
  const [isRunningMatching, setIsRunningMatching] = useState(false);

  const handleDelete = async () => {
    if (!currentTenantId || !deleteTarget) return;
    await deleteInvoice(currentTenantId, deleteTarget.id);
    setDeleteTarget(null);
    fetchInvoices();
  };

  const handleReprocess = async () => {
    if (!currentTenantId || !reprocessTarget) return;
    setIsReprocessing(true);
    try {
      await retryExtraction(currentTenantId, reprocessTarget.id);
      setReprocessTarget(null);
      // WebSocket will notify us when processing completes
    } catch (err) {
      console.error('Reprocess failed:', err);
      setError(err instanceof Error ? err.message : 'Reprocess failed');
    } finally {
      setIsReprocessing(false);
    }
  };

  const handleRunMatching = async () => {
    if (!currentTenantId || !matchingTarget) return;
    setIsRunningMatching(true);
    try {
      await retryMatching(currentTenantId, matchingTarget.id);
      setMatchingTarget(null);
      // WebSocket will notify us when matching completes
    } catch (err) {
      console.error('Run matching failed:', err);
      setError(err instanceof Error ? err.message : 'Run matching failed');
    } finally {
      setIsRunningMatching(false);
    }
  };

  const fetchInvoices = useCallback(async () => {
    if (!currentTenantId) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await listInvoices(currentTenantId);
      setInvoices(data);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
      setError(err instanceof Error ? err.message : 'Failed to load invoices');
    } finally {
      setIsLoading(false);
    }
  }, [currentTenantId]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Subscribe to real-time invoice updates via WebSocket
  useWebSocketSubscription(
    'invoice.updated',
    () => {
      // Refresh the list when any invoice is updated
      fetchInvoices();
    },
    [fetchInvoices]
  );

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !currentTenantId) return;

    setIsUploading(true);
    setError(null);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Validate file type
        if (file.type !== 'application/pdf') {
          throw new Error(`Invalid file type: ${file.name}. Only PDF files are allowed.`);
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          throw new Error(`File too large: ${file.name}. Maximum size is 10MB.`);
        }

        setUploadProgress(`Uploading ${file.name}... (${i + 1}/${files.length})`);

        // Get presigned URL
        const { uploadUrl } = await getInvoiceUploadUrl(currentTenantId, file.name, file.type);

        // Upload to S3
        await uploadInvoiceToS3(uploadUrl, file);
      }

      setUploadProgress(null);
      // Refresh invoice list and hide upload area
      await fetchInvoices();
      setShowUploadArea(false);
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const filteredInvoices = invoices.filter((inv) => {
    // Status filter
    if (statusFilter !== 'all' && inv.status !== statusFilter) return false;

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesInvoiceNumber = inv.invoiceNumber?.toLowerCase().includes(query);
      const matchesVendorName = inv.vendorName?.toLowerCase().includes(query);
      const matchesMatchedVendor = inv.matchedVendorName?.toLowerCase().includes(query);
      if (!matchesInvoiceNumber && !matchesVendorName && !matchesMatchedVendor) return false;
    }

    return true;
  });

  // Sort handler
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort invoices
  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    const direction = sortDirection === 'asc' ? 1 : -1;

    switch (sortColumn) {
      case 'invoiceNumber':
        return direction * (a.invoiceNumber || '').localeCompare(b.invoiceNumber || '');
      case 'vendor': {
        const vendorA = a.matchedVendorName || a.vendorName || '';
        const vendorB = b.matchedVendorName || b.vendorName || '';
        return direction * vendorA.localeCompare(vendorB);
      }
      case 'invoiceDate':
        return direction * (a.invoiceDate || '').localeCompare(b.invoiceDate || '');
      case 'dueDate':
        return direction * (a.dueDate || '').localeCompare(b.dueDate || '');
      case 'total':
        return direction * ((a.total ?? 0) - (b.total ?? 0));
      case 'currency':
        return direction * (a.currency || '').localeCompare(b.currency || '');
      case 'status':
        return direction * a.status.localeCompare(b.status);
      case 'createdAt':
        return direction * (a.createdAt || '').localeCompare(b.createdAt || '');
      default:
        return 0;
    }
  });

  const formatAmount = (amount: number | undefined) => {
    if (amount === undefined) return '';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '';
    // Handle YYYY-MM-DD format - return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    // Handle ISO timestamps (createdAt, updatedAt) - extract date portion
    return dateStr.split('T')[0];
  };

  // Sort indicator component
  const SortHeader = ({
    column,
    children,
    align = 'left',
  }: {
    column: SortColumn;
    children: React.ReactNode;
    align?: 'left' | 'right';
  }) => (
    <th
      onClick={() => handleSort(column)}
      className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortColumn === column ? (
          <svg
            className={`w-3 h-3 transition-transform ${sortDirection === 'desc' ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        ) : (
          <svg
            className="w-3 h-3 text-gray-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
            />
          </svg>
        )}
      </span>
    </th>
  );

  // Amount validation indicator
  const AmountValidationIndicator = ({
    status,
  }: {
    status: 'valid' | 'invalid' | 'unknown' | undefined;
  }) => {
    if (status === 'valid') {
      return (
        <span className="text-emerald-500 ml-1" title="Amounts valid">
          ✓
        </span>
      );
    }
    if (status === 'invalid') {
      return (
        <span className="text-red-500 ml-1" title="Amount discrepancy">
          ✗
        </span>
      );
    }
    // unknown or undefined - don't show anything
    return null;
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">{t('nav.invoices')}</h1>
        <div className="flex items-center gap-3">
          {/* Search field */}
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search invoices..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8 py-2 w-80 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
          <button
            onClick={() => setShowUploadArea(!showUploadArea)}
            disabled={isUploading}
            className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed ${
              showUploadArea
                ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                : 'border-transparent text-white bg-primary-600 hover:bg-primary-700'
            }`}
          >
            {isUploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Uploading...
              </>
            ) : showUploadArea ? (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Cancel
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Upload Invoice
              </>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            multiple
            className="hidden"
            onChange={(e) => handleUpload(e.target.files)}
          />
        </div>
      </div>

      {/* Upload progress */}
      {uploadProgress && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
            <span className="text-sm text-blue-700">{uploadProgress}</span>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Drag and drop zone */}
      {showUploadArea && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`mb-6 border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragOver
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          <p className="mt-2 text-sm text-gray-600">
            Drag and drop PDF invoices here, or{' '}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-primary-600 hover:text-primary-500 font-medium"
            >
              browse
            </button>
          </p>
          <p className="mt-1 text-xs text-gray-500">PDF files only, max 10MB each</p>
        </div>
      )}

      {/* Status filter */}
      <div className="mb-4 flex gap-2 flex-wrap">
        {(
          [
            'all',
            'pending',
            'processing',
            'pending_approval',
            'approved',
            'rejected',
            'extraction_failed',
          ] as const
        ).map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-3 py-1 text-sm rounded-full transition-colors ${
              statusFilter === status
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {status === 'all' ? 'All' : STATUS_LABELS[status]}
          </button>
        ))}
      </div>

      {/* Invoice list */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : filteredInvoices.length === 0 ? (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="mt-2 text-sm text-gray-500">
            {statusFilter === 'all'
              ? 'No invoices yet. Upload your first invoice to get started.'
              : `No ${statusFilter} invoices found.`}
          </p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortHeader column="invoiceNumber">Invoice</SortHeader>
                <SortHeader column="vendor">Vendor</SortHeader>
                <SortHeader column="createdAt">Received</SortHeader>
                <SortHeader column="invoiceDate">Date</SortHeader>
                <SortHeader column="dueDate">Due Date</SortHeader>
                <SortHeader column="total" align="right">
                  Amount
                </SortHeader>
                <SortHeader column="currency">Currency</SortHeader>
                <SortHeader column="status">Status</SortHeader>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedInvoices.map((invoice) => (
                <tr
                  key={invoice.id}
                  onClick={() => navigate(`/invoices/${invoice.id}`)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div
                      className={`text-sm font-medium ${
                        invoice.status === 'DUPLICATE_FILE' ? 'text-red-600' : 'text-primary-600'
                      }`}
                    >
                      {invoice.invoiceNumber || 'UNKNOWN'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {invoice.vendorMatched ? (
                      <div>
                        <div className="text-sm text-gray-900">{invoice.matchedVendorName}</div>
                        {invoice.vendorName && invoice.vendorName !== invoice.matchedVendorName && (
                          <div className="text-xs text-gray-400">{invoice.vendorName}</div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        {invoice.status !== 'DUPLICATE_FILE' && (
                          <span
                            className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0"
                            title="Vendor not matched"
                          />
                        )}
                        <span className="text-sm text-gray-900">{invoice.vendorName || ''}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(invoice.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(invoice.invoiceDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(invoice.dueDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    <span className="inline-flex items-center">
                      {formatAmount(invoice.total)}
                      <AmountValidationIndicator status={invoice.amountValidation} />
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {invoice.currency || ''}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        STATUS_COLORS[invoice.status] || 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {STATUS_LABELS[invoice.status] || invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      {/* Run Matching button - hidden for duplicates */}
                      {invoice.status !== 'DUPLICATE_FILE' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setMatchingTarget(invoice);
                          }}
                          className="text-gray-400 hover:text-primary-600 transition-colors"
                          title="Run matching rules"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                            />
                          </svg>
                        </button>
                      )}
                      {/* Reprocess button - hidden for duplicates */}
                      {invoice.status !== 'DUPLICATE_FILE' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setReprocessTarget(invoice);
                          }}
                          className="text-gray-400 hover:text-primary-600 transition-colors"
                          title="Reprocess invoice"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                          </svg>
                        </button>
                      )}
                      {/* Delete button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(invoice);
                        }}
                        className="text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete invoice"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteInvoiceModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        invoiceNumber={deleteTarget?.invoiceNumber || deleteTarget?.id.slice(0, 8) || ''}
      />

      {/* Reprocess Confirmation Modal */}
      <ReprocessInvoiceModal
        isOpen={reprocessTarget !== null}
        onClose={() => setReprocessTarget(null)}
        onConfirm={handleReprocess}
        invoiceNumber={reprocessTarget?.invoiceNumber || reprocessTarget?.id.slice(0, 8) || ''}
        isProcessing={isReprocessing}
      />

      {/* Run Matching Confirmation Modal */}
      <RunMatchingModal
        isOpen={matchingTarget !== null}
        onClose={() => setMatchingTarget(null)}
        onConfirm={handleRunMatching}
        invoiceNumber={matchingTarget?.invoiceNumber || matchingTarget?.id.slice(0, 8) || ''}
        isProcessing={isRunningMatching}
      />
    </div>
  );
}
